import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Server, Mail, HardDrive, Calendar, Check, Loader, Folder, FolderPlus } from 'lucide-react';
import Button from '../Common/Button';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled(motion.div)`
  width: 600px;
  max-height: 80vh;
  background: ${({ theme }) => theme.colors.dark.surface};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.dark.border};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[500]};
    color: ${({ theme }) => theme.colors.primary[400]};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const CloseButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.status.error};
    color: ${({ theme }) => theme.colors.status.error};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ModalContent = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-height: 60vh;
  overflow-y: auto;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const ServiceCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme, isConnected }) => 
    isConnected ? theme.colors.status.online : theme.colors.dark.border
  };
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  cursor: ${({ isConnected }) => isConnected ? 'default' : 'pointer'};
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;
  
  &:hover {
    ${({ isConnected, theme }) => !isConnected && `
      border-color: ${theme.colors.primary[500]};
      background: ${theme.colors.primary[500]}05;
    `}
  }
`;

const ServiceIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${({ service }) => {
    switch (service) {
      case 'gmail': return '#ea4335';
      case 'drive': return '#4285f4';
      case 'calendar': return '#fbbc04';
      default: return '#6b7280';
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ServiceName = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const ServiceStatus = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: 0.75rem;
  color: ${({ theme, isConnected }) => 
    isConnected ? theme.colors.status.online : theme.colors.dark.textSecondary
  };
`;

const ConnectedBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm};
  right: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.status.online};
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const LocalOptionGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const LocalOptionCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[500]};
    background: ${({ theme }) => theme.colors.primary[500]}05;
  }
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const OptionIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.primary[500]}20;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary[500]};
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const OptionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark.text};
  margin: 0;
`;

const OptionDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  margin: 0;
  line-height: 1.4;
`;

const AddConnectionModal = ({ onClose, onConnect, serviceConfig, connections, connectingService }) => {
  // Build services list from serviceConfig with connection status
  const services = Object.keys(serviceConfig).map(key => {
    const service = serviceConfig[key];
    const connection = connections.find(conn => conn.service === key);
    const ServiceIcon = service.icon;
    
    return {
      id: key,
      name: service.name,
      icon: ServiceIcon,
      color: service.color,
      isConnected: connection && connection.connected,
      account: connection?.service_email
    };
  });

  const localOptions = [
    {
      id: 'full',
      title: 'Full Storage Access',
      description: 'Grant SARVIK access to your entire file system. Provides maximum flexibility but requires elevated permissions.',
      icon: HardDrive
    },
    {
      id: 'folder',
      title: 'Folder Access',
      description: 'Choose specific folders for SARVIK to access. More secure and controlled approach.',
      icon: Folder
    },
    {
      id: 'sandbox',
      title: 'Create Sandbox Folder',
      description: 'Create a dedicated folder where SARVIK can safely store and access files.',
      icon: FolderPlus
    }
  ];

  const handleServiceConnect = (service) => {
    if (service.isConnected) return;
    
    // Call the real connect handler from parent
    onConnect(service.id);
    onClose();
  };

  const handleLocalConnect = async (option) => {
    let newConnection = {
      category: 'local',
      status: 'connected',
      lastAccess: 'Just now'
    };

    if (window.electronAPI) { // Check Electron API is available
      switch (option.id) {
        case 'full':
          newConnection = {
            ...newConnection,
            type: 'local',
            name: 'Full System Access',
            path: 'System Wide',
            permissions: 'Full Access'
          };
          break;
        case 'folder':
          // Open native folder picker
          const result = await window.electronAPI.selectFolder();
          if (result && result.success && result.path) {
            newConnection = {
              ...newConnection,
              type: 'local',
              name: `Folder: ${result.name}`,
              path: result.path,
              permissions: 'Folder Access'
            };
          } else {
            // Optionally show a toast/error here
            return;
          }
          break;
        case 'sandbox':
          // Create SARVIK sandbox folder
          const sandboxResult = await window.electronAPI.createSandboxFolder('SARVIK');
          if (sandboxResult && sandboxResult.success && sandboxResult.path) {
            newConnection = {
              ...newConnection,
              type: 'local',
              name: 'SARVIK Sandbox',
              path: sandboxResult.path,
              permissions: 'Sandbox Access'
            };
          } else {
            // Optionally show a toast/error here
            return;
          }
          break;
      }
    } else {
      // Fallback or simulated connection for browser only
      switch (option.id) {
        case 'full':
          newConnection = {
            ...newConnection,
            type: 'local',
            name: 'Full System Access',
            path: 'System Wide',
            permissions: 'Full Access'
          };
          break;
        case 'folder':
          newConnection = {
            ...newConnection,
            type: 'local',
            name: 'Selected Folders',
            path: '~/Documents, ~/Downloads',
            permissions: 'Folder Access'
          };
          break;
        case 'sandbox':
          newConnection = {
            ...newConnection,
            type: 'local',
            name: 'SARVIK Sandbox',
            path: '~/Documents/SARVIK',
            permissions: 'Sandbox Access'
          };
          break;
      }
    }

    // Save or add connection to UI
    // onConnectionAdded(newConnection);
  };

  const renderServices = () => (
    <ServiceGrid>
      {services.map((service) => {
        const ServiceIconComponent = service.icon;
        const isConnecting = connectingService === service.id;
        
        return (
          <ServiceCard
            key={service.id}
            isConnected={service.isConnected}
            whileHover={!service.isConnected ? { scale: 1.02 } : {}}
            whileTap={!service.isConnected ? { scale: 0.98 } : {}}
            onClick={() => handleServiceConnect(service)}
          >
            {service.isConnected && (
              <ConnectedBadge>
                <Check />
              </ConnectedBadge>
            )}
            
            <ServiceIcon service={service.id} style={{ background: service.color }}>
              <ServiceIconComponent style={{ width: '20px', height: '20px' }} />
            </ServiceIcon>
            
            <ServiceName>{service.name}</ServiceName>
            
            <ServiceStatus isConnected={service.isConnected}>
              {isConnecting ? (
                <>
                  <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  Connecting...
                </>
              ) : service.isConnected ? (
                <>
                  <Check size={12} />
                  Connected
                </>
              ) : (
                'Click to connect'
              )}
            </ServiceStatus>
            
            {service.account && (
              <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '4px' }}>
                {service.account}
              </div>
            )}
          </ServiceCard>
        );
      })}
    </ServiceGrid>
  );

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContainer
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <HeaderLeft>
            <ModalTitle>Add Connection</ModalTitle>
          </HeaderLeft>
          
          <CloseButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
          >
            <X />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <div style={{ marginBottom: '16px', fontSize: '0.875rem', color: '#999' }}>
            Choose a service to connect to SARVIK
          </div>
          {renderServices()}
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default AddConnectionModal;